import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import {
    Autocomplete,
    Backdrop,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    Divider,
    FormControlLabel,
    FormHelperText,
    ListItem,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, MobileDatePicker } from '@mui/x-date-pickers-pro';
import { useMuiController, useMuiForm } from '@zenput/util-common';
import { endOfDay } from 'date-fns';
import { useSnackbar } from 'notistack';
import { Controller, FieldError, useWatch } from 'react-hook-form';
import { makeStyles } from 'tss-react/mui';
import { useDebounce } from 'usehooks-ts';
import { ZodIssueCode } from 'zod';
import { FormTemplates, LocationUsersV3 } from '../../../backbone-unorganized/shared/forms';
import AppBar from '../../shared/AppBar';
import BottomBar from '../../shared/BottomBar';
import { ErrorFooterAlert } from '../../shared/ErrorFooterAlert.tsx/ErrorFooterAlert';
import PageLayout from '../../shared/PageLayout';
import { useBackbone } from '../providers/BackboneProvider';
import { toDate } from './FollowUpActionForm';
import {
    FollowUpActionType,
    IncompleteFollowUpActionType,
    IncompleteFollowUpActionSchema,
    ReplyType,
    FollowUpActionImageType,
    FollowUpActionFileType,
    TaskItemContext,
    AllReplyTypes
} from './FollowUpActions.types';
import { FollowUpActionsGallery } from './FollowUpActionsGallery';
import { FUAReplies } from './ShowFollowUpAction';
import { IdLabelPair, LegacyApiModel } from './types';

const useStyles = makeStyles()((theme) => {
    const { spacing, typography } = theme;

    return {
        autoSelect: {
            marginTop: spacing(2),
            borderBottom: 'groove',
            '& .MuiInputBase-input': {
                minHeight: '36px',
                border: 'none',
                background: 'none',
                padding: `${spacing(2)} 0`,
                color: 'black !important'
            },
            '& .MuiFormControl-root': {
                height: 'auto'
            },
            '& .MuiInputLabel-filled': {
                paddingTop: spacing(0.25)
            }
        },
        bottomBar: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            padding: spacing(2),
            textTransform: 'none',
            backgroundColor: 'white'
        },
        bottomButton: {
            textTransform: 'none',
            padding: `${spacing(1.5)} 0`
        },
        formContainer: {
            backgroundColor: 'white',
            marginTop: spacing(2),
            marginBottom: spacing(10),
            padding: spacing(2)
        },
        formInput: {
            marginTop: spacing(2),
            '& .MuiInputBase-input': {
                border: 'none',
                padding: `${spacing(2)} 0`,
                borderBottom: 'groove'
            },
            '& .MuiInputBase-input:focus': {
                borderColor: 'currentcolor !important'
            },
            '& .MuiFilledInput-input': {
                paddingLeft: spacing(1.5),
                paddingTop: spacing(4.5),
                color: 'black !important'
            },
            '& .MuiInputLabel-filled': {
                paddingTop: spacing(0.25)
            }
        },
        textFieldInput: {
            marginTop: spacing(2),
            borderBottom: 'groove',
            '& .MuiInputBase-input': {
                border: 'none',
                background: 'none'
            },
            '& .MuiInputBase-input:focus': {
                borderColor: 'currentcolor !important'
            },
            '& .MuiFilledInput-input': {
                fontSize: typography.body2.fontSize,
                color: 'black !important'
            }
        },
        dateFieldInput: {
            marginTop: spacing(2),
            borderBottom: 'groove',
            '& .MuiInputBase-input': {
                padding: `${spacing(2.5)} 0`,
                border: 'none',
                color: 'black !important'
            },
            '& .MuiInputBase-input:focus': {
                borderColor: 'currentcolor !important'
            },
            '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
                borderBottom: 'groove'
            },
            '& .MuiOutlinedInput-notchedOutline:focus': {
                borderColor: 'currentcolor !important'
            },
            '& .MuiFilledInput-input': {
                paddingLeft: spacing(1.5),
                paddingTop: spacing(4.5),
                color: 'black !important'
            },
            '& .MuiInputLabel-filled': {
                paddingTop: spacing(0.25)
            }
        }
    };
});

interface EditSavedFollowUpActionProps {
    followUpAction: FollowUpActionType;
    onGoBack: () => void;
    open: boolean;
    onSuccessfulUpdate: (followUpAction: FollowUpActionType) => void;
}

export const EditSavedFollowUpAction = (props: EditSavedFollowUpActionProps) => {
    const { followUpAction, onGoBack, open, onSuccessfulUpdate } = props;
    const { classes } = useStyles();
    const { allowFollowers, formReplyEnabled, getS3URL, isAdmin, uploadFilesToS3 } = useBackbone<TaskItemContext>();
    const { enqueueSnackbar } = useSnackbar();
    const convertReplyType = (replyType: string): ReplyType => {
        if (replyType === FUAReplies.BASIC || replyType === FUAReplies.PHOTO) {
            return AllReplyTypes[0];
        }
        if (replyType === FUAReplies.FORM) {
            return AllReplyTypes[1];
        }
        throw Error('Unknown reply type');
    };
    const {
        clearErrors,
        control,
        handleSubmit,
        formState: { errors }
    } = useMuiForm<IncompleteFollowUpActionType>({
        resolver: zodResolver(IncompleteFollowUpActionSchema),
        defaultValues: {
            assigned_user: followUpAction.assigned_user,
            description: followUpAction.data.description!,
            due_date: toDate(followUpAction.date_due!)!,
            files: [],
            followers: followUpAction.followers ?? [],
            form: followUpAction.data.reply?.form || null,
            reply_type: convertReplyType(followUpAction.reply_type),
            require_photo: followUpAction.reply_type === FUAReplies.PHOTO,
            title: followUpAction.title
        }
    });

    const getError = (error?: FieldError) => {
        const zodErrorCodeMap: Record<string, string | undefined> = {
            custom: _t('mobile_mobileform_add_follow_up_actions_forms_required'),
            invalid_type: _t('mobile_mobileform_add_follow_up_actions_forms_required'),
            too_small: _t('mobile_mobileform_add_follow_up_actions_forms_required'),
            too_big: _t('mobile_mobileform_add_follow_up_actions_forms_too_many_characters')
        } satisfies Partial<Record<ZodIssueCode, string>>;

        return error
            ? {
                  error: true,
                  helperText: zodErrorCodeMap[error.type] ?? _t('mobile_mobileform_add_follow_up_actions_forms_generic')
              }
            : {};
    };

    const replyType = useWatch({ name: 'reply_type', control });

    const requiredOrderedFieldsTask = ['title', 'description', 'due_date'];
    const requiredOrderedFieldsForm = ['title', 'description', 'due_date', 'form'];
    const [requiredOrderedFields, setRequiredOrderedFields] = useState(requiredOrderedFieldsTask);

    const { field: assigneeField } = useMuiController({ control, name: 'assigned_user' });
    const { field: descriptionField } = useMuiController({ control, name: 'description' });
    const { field: dueDateField } = useMuiController({ control, name: 'due_date' });
    const { field: formField } = useMuiController({ control, name: 'form' });
    const { field: requirePhotoField } = useMuiController({ control, name: 'require_photo' });
    const { field: titleField } = useMuiController({ control, name: 'title' });

    const [savedFiles, setSavedFiles] = useState<FollowUpActionImageType[]>(
        followUpAction.data.images?.map((filePath) => ({ s3_key: filePath }) as FollowUpActionImageType) ?? []
    );
    const [uploadedFiles, setUploadedFiles] = useState<FollowUpActionFileType[]>([]);
    const [showSpinner, setShowSpinner] = useState(false);
    const [assignees, setAssignees] = useState<{ id: number; display_name: string }[]>([]);
    const [assigneeSearch, setAssigneeSearch] = useState('');
    const debouncedAssigneeSearch = useDebounce(assigneeSearch, 200);
    const [followerOptions, setFollowerOptions] = useState<{ id: number; display_name: string }[]>([]);
    const [followerSearch, setFollowerSearch] = useState('');
    const debouncedFollowerSearch = useDebounce(followerSearch, 200);
    const [formSearch, setFormSearch] = useState('');
    const debouncedFormSearch = useDebounce(formSearch, 200);
    const [forms, setForms] = useState<IdLabelPair[]>([]);

    useEffect(() => {
        const mobileAssignees = new LocationUsersV3([]);
        mobileAssignees.location_id = followUpAction.location.id;
        mobileAssignees.paginator.set('search', debouncedAssigneeSearch);

        mobileAssignees.fetch({
            success: function () {
                const assigneeData = mobileAssignees.models.map((model: LegacyApiModel) => ({
                    id: model.get('id'),
                    display_name: model.get('display_name')
                }));
                setAssignees(assigneeData);
            },
            error: function () {
                enqueueSnackbar(JSON.stringify(_t('mobile_mobileform_add_follow_up_actions_error_users')), {
                    variant: 'error'
                });
            }
        });
    }, [debouncedAssigneeSearch]);

    useEffect(() => {
        if (!allowFollowers) {
            return;
        }
        const mobileFollowers = new LocationUsersV3([]);
        mobileFollowers.location_id = followUpAction.location.id;
        mobileFollowers.paginator.set('search', debouncedFollowerSearch);

        mobileFollowers.fetch({
            success: function () {
                const followerData = mobileFollowers.models.map((model: LegacyApiModel) => ({
                    id: model.get('id'),
                    display_name: model.get('display_name')
                }));
                setFollowerOptions(followerData);
            },
            error: function () {
                enqueueSnackbar(JSON.stringify(_t('mobile_mobileform_add_follow_up_actions_error_users')), {
                    variant: 'error'
                });
            }
        });
    }, [debouncedFollowerSearch, allowFollowers]);

    useEffect(() => {
        if (!formReplyEnabled) {
            return;
        }
        const formTemplates = new FormTemplates([], { search: debouncedFormSearch });
        formTemplates.paginator.set('filter', isAdmin ? 'all' : 'me');
        formTemplates.fetch({
            success: function () {
                const formsData = formTemplates.models.map((model: LegacyApiModel) => ({
                    id: model.get('id'),
                    label: model.get('title') ?? ''
                }));
                setForms(formsData);
            },
            error: function () {
                enqueueSnackbar(JSON.stringify(_t('mobile_mobileform_add_follow_up_actions_error_forms')), {
                    variant: 'error'
                });
            }
        });
    }, [debouncedFormSearch, formReplyEnabled]);

    useEffect(() => {
        setRequiredOrderedFields(replyType === 'form' ? requiredOrderedFieldsForm : requiredOrderedFieldsTask);
        clearErrors();
    }, [replyType]);

    const onSubmit = handleSubmit(async (values) => {
        const images: string[] = [
            ...(savedFiles.filter((file) => Boolean(file.s3_key)).map((file) => file.s3_key) as string[]),
            ...(uploadedFiles
                .filter((file) => Boolean(file.s3_key))
                .map((uploadedFile) => uploadedFile.s3_key) as string[])
        ];

        followUpAction.assigned_user = values.assigned_user ?? null;
        if (allowFollowers) {
            followUpAction.followers = values.followers ?? [];
        }
        followUpAction.date_due = values.due_date;
        if (formReplyEnabled && values.reply_type === 'form') {
            followUpAction.reply_type = FUAReplies.FORM;
        } else if (values.require_photo) {
            followUpAction.reply_type = FUAReplies.PHOTO;
        } else {
            followUpAction.reply_type = FUAReplies.BASIC;
        }
        followUpAction.title = values.title;
        followUpAction.data = {
            description: values.description,
            images,
            reply: followUpAction.reply_type === FUAReplies.FORM && values.form ? { form: values.form } : {}
        };

        await onSuccessfulUpdate(followUpAction);
    });

    const handleOnDeleteSavedImage = (indexToRemove: number) => {
        setSavedFiles((prevImages) => prevImages.filter((_, index) => indexToRemove !== index));
    };

    const handleOnUploadImages = (images: FollowUpActionFileType[]) => {
        setUploadedFiles((prevImages) => [...prevImages, ...images]);
    };

    const handleOnDeleteUploadedImage = (indexToRemove: number) => {
        setUploadedFiles((prevImages) => prevImages.filter((_, index) => indexToRemove !== index));
    };

    const getToday = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today;
    };

    return (
        <Dialog
            fullScreen
            open={open}
            onClose={onGoBack}
            slotProps={{
                paper: {
                    sx: {
                        overflow: 'hidden'
                    }
                }
            }}
        >
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <PageLayout>
                    {{
                        appBar: (
                            <AppBar
                                onGoBack={onGoBack}
                                title={_t('mobile_mobileform_add_follow_up_actions_forms_update_button')}
                            />
                        ),
                        content: (
                            <Container component="div" className={classes.formContainer}>
                                {showSpinner && (
                                    <Backdrop open sx={{ zIndex: (theme) => theme.zIndex.appBar - 1 }}>
                                        <CircularProgress />
                                    </Backdrop>
                                )}

                                <Stack component="form" onSubmit={onSubmit}>
                                    {formReplyEnabled && (
                                        <Controller
                                            name="reply_type"
                                            control={control}
                                            render={({ field }) => {
                                                const { value, onChange } = field;
                                                return (
                                                    <ToggleButtonGroup
                                                        color="primary"
                                                        exclusive
                                                        fullWidth
                                                        onChange={(_, newValue) => {
                                                            if (newValue) {
                                                                onChange(newValue);
                                                            }
                                                        }}
                                                        sx={{ paddingBottom: 2 }}
                                                        size="large"
                                                        unselectable="off"
                                                        value={value}
                                                    >
                                                        <ToggleButton
                                                            aria-label={_t('mobile_common_task')}
                                                            value={AllReplyTypes[0]}
                                                        >
                                                            <CheckBoxOutlinedIcon />
                                                            <Typography paddingLeft={1}>
                                                                {_t('mobile_common_task')}
                                                            </Typography>
                                                        </ToggleButton>
                                                        <ToggleButton
                                                            aria-label={_t('mobile_task_tab_form')}
                                                            value={AllReplyTypes[1]}
                                                        >
                                                            <AssignmentOutlinedIcon />
                                                            <Typography paddingLeft={1}>
                                                                {_t('mobile_task_tab_form')}{' '}
                                                            </Typography>
                                                        </ToggleButton>
                                                    </ToggleButtonGroup>
                                                );
                                            }}
                                        />
                                    )}
                                    <FollowUpActionsGallery
                                        getS3URL={getS3URL}
                                        isReply={false}
                                        onDeleteSavedImage={handleOnDeleteSavedImage}
                                        onDeleteUploadedImage={handleOnDeleteUploadedImage}
                                        onUploadFiles={handleOnUploadImages}
                                        photoQualityAnalysis={false}
                                        savedFiles={savedFiles}
                                        uploadedFiles={uploadedFiles}
                                        uploadFilesToS3={uploadFilesToS3}
                                        onLoadingChange={setShowSpinner}
                                    />

                                    <TextField
                                        className={classes.formInput}
                                        id="title"
                                        label={_t('mobile_mobileform_add_follow_up_actions_forms_title')}
                                        required
                                        variant="filled"
                                        {...titleField}
                                        {...getError(errors.title)}
                                    />

                                    <TextField
                                        className={classes.textFieldInput}
                                        data-testid="controlled-input2"
                                        error={getError(errors.description).error}
                                        id="description"
                                        multiline
                                        label={_t('mobile_mobileform_add_follow_up_actions_forms_description')}
                                        required
                                        variant="filled"
                                        {...descriptionField}
                                    />
                                    {errors.description && (
                                        <FormHelperText sx={{ marginLeft: 2 }} error>
                                            {getError(errors.description).helperText}
                                        </FormHelperText>
                                    )}

                                    {(!formReplyEnabled || replyType == 'task') && (
                                        <FormControlLabel
                                            sx={{ padding: 1 }}
                                            control={
                                                <Checkbox
                                                    checked={requirePhotoField.value}
                                                    sx={{ marginLeft: 1 }}
                                                    {...requirePhotoField}
                                                />
                                            }
                                            label={_t('mobile_mobileform_add_follow_up_actions_forms_photo')}
                                        />
                                    )}
                                    {replyType == 'form' && (
                                        <Autocomplete
                                            className={classes.autoSelect}
                                            disablePortal
                                            getOptionKey={(option) => option.id}
                                            getOptionLabel={(option) => option.label}
                                            id="form"
                                            isOptionEqualToValue={(option, value) =>
                                                value === null || option.id === value.id
                                            }
                                            onChange={(_event, newValue) => {
                                                formField.onChange(newValue);
                                            }}
                                            options={forms}
                                            value={formField.value ?? null}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    error={getError(errors.form as FieldError).error}
                                                    label={_t('mobile_task_tab_form')}
                                                    onBlur={() => setFormSearch('')}
                                                    onChange={(e) => setFormSearch(e.target.value)}
                                                    required
                                                    slotProps={{
                                                        htmlInput: {
                                                            ...params.inputProps,
                                                            required: true,
                                                            'data-testid': 'form-autocomplete'
                                                        }
                                                    }}
                                                    sx={{
                                                        height: (theme) => theme.spacing(15)
                                                    }}
                                                    value={formSearch}
                                                    variant="filled"
                                                />
                                            )}
                                            renderOption={(renderProps, option) => (
                                                <ListItem {...renderProps}>{option.label}</ListItem>
                                            )}
                                        />
                                    )}
                                    {errors.form && (
                                        <FormHelperText sx={{ marginLeft: 2 }} error>
                                            {getError(errors.form as FieldError).helperText}
                                        </FormHelperText>
                                    )}
                                    <MobileDatePicker
                                        className={classes.dateFieldInput}
                                        label={_t('mobile_mobileform_add_follow_up_actions_forms_due_date')}
                                        minDate={getToday()}
                                        slotProps={{
                                            textField: {
                                                error: getError(errors.due_date).error,
                                                id: 'due_date',
                                                required: true,
                                                slotProps: { input: { endAdornment: <CalendarTodayIcon /> } },
                                                variant: 'filled'
                                            }
                                        }}
                                        value={dueDateField.value ?? null}
                                        onChange={(newDate) => {
                                            dueDateField.onChange(newDate ? endOfDay(newDate) : null);
                                        }}
                                    />
                                    {errors.due_date && (
                                        <FormHelperText sx={{ marginLeft: 2 }} error>
                                            {getError(errors.due_date).helperText}
                                        </FormHelperText>
                                    )}

                                    <Autocomplete
                                        className={classes.autoSelect}
                                        disablePortal
                                        getOptionKey={(option) => option.id}
                                        getOptionLabel={(option) => option.display_name}
                                        onChange={(_event, newValue, reason) => {
                                            assigneeField.onChange(newValue);
                                            if (reason === 'selectOption') {
                                                // Native App leaves the soft keyboard open after selection
                                                // since the input keeps focus; blur it to dismiss.
                                                (document.activeElement as HTMLElement | null)?.blur();
                                            }
                                        }}
                                        options={assignees}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={_t('mobile_common_assigned_to')}
                                                onBlur={() => setAssigneeSearch('')}
                                                onChange={(e) => setAssigneeSearch(e.target.value)}
                                                sx={{ height: 60 }}
                                                value={assigneeSearch}
                                                variant="filled"
                                                error={getError(errors.assigned_user as FieldError).error}
                                            />
                                        )}
                                        value={assigneeField.value ?? null}
                                    />

                                    {'assigned_user' in errors && (
                                        <FormHelperText sx={{ marginLeft: 2 }} error>
                                            {getError(errors.assigned_user as FieldError).helperText}
                                        </FormHelperText>
                                    )}

                                    {allowFollowers && (
                                        <Controller
                                            name="followers"
                                            control={control}
                                            render={({ field }) => {
                                                const { onChange, value } = field;
                                                const selectedFollowers = value ?? [];
                                                const availableFollowerOptions = followerOptions.filter(
                                                    (option) =>
                                                        !selectedFollowers.some((follower) => follower.id === option.id)
                                                );

                                                const handleFollowerDeletion = (followerId: number | string) => {
                                                    onChange(
                                                        selectedFollowers.filter(
                                                            (follower) => follower.id !== followerId
                                                        )
                                                    );
                                                };

                                                return (
                                                    <Stack>
                                                        <Divider sx={{ marginTop: 2 }} />
                                                        <Autocomplete
                                                            blurOnSelect
                                                            className={classes.autoSelect}
                                                            disablePortal
                                                            getOptionKey={(option) => option.id}
                                                            getOptionLabel={(option) => option.display_name}
                                                            isOptionEqualToValue={(option, optionValue) =>
                                                                option.id === optionValue.id
                                                            }
                                                            onChange={(_event, newValue) => {
                                                                if (newValue) {
                                                                    onChange([...selectedFollowers, newValue]);
                                                                }
                                                                setFollowerSearch('');
                                                            }}
                                                            options={availableFollowerOptions}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    label={_t('mobile_tasks_followers')}
                                                                    onBlur={() => setFollowerSearch('')}
                                                                    onChange={(e) => setFollowerSearch(e.target.value)}
                                                                    sx={{ height: 60 }}
                                                                    value={followerSearch}
                                                                    variant="filled"
                                                                />
                                                            )}
                                                            value={null}
                                                        />
                                                        {selectedFollowers.length > 0 && (
                                                            <Stack
                                                                direction="row"
                                                                flexWrap="wrap"
                                                                gap={1}
                                                                sx={{ marginTop: 1 }}
                                                            >
                                                                {selectedFollowers.map((follower) => (
                                                                    <Chip
                                                                        color="primary"
                                                                        key={follower.id}
                                                                        label={follower.display_name}
                                                                        onDelete={() =>
                                                                            handleFollowerDeletion(follower.id)
                                                                        }
                                                                    />
                                                                ))}
                                                            </Stack>
                                                        )}
                                                    </Stack>
                                                );
                                            }}
                                        />
                                    )}
                                </Stack>
                            </Container>
                        ),
                        bottomBar: (
                            <BottomBar>
                                {Object.keys(errors).length > 0 ? (
                                    <ErrorFooterAlert errors={errors} requiredOrderedFields={requiredOrderedFields} />
                                ) : (
                                    <div className={classes.bottomBar}>
                                        <Button
                                            className={classes.bottomButton}
                                            color="primary"
                                            data-testid="addFollowUpButton"
                                            disabled={showSpinner}
                                            onClick={onSubmit}
                                            variant="contained"
                                        >
                                            {_t('mobile_mobileform_add_follow_up_actions_forms_update_button')}
                                        </Button>
                                    </div>
                                )}
                            </BottomBar>
                        )
                    }}
                </PageLayout>
            </LocalizationProvider>
        </Dialog>
    );
};
